import {
	setCompletedProjects,
	setInProgressProjects,
	setNotStartedProjects
} from '@features/project-management-tools/redux/myProjectSlice';
import {
	setGroupList,
	setOriginalTaskList,
	setProjectDetails,
	setProjectMembers,
	setSelectedColumnToHide,
	setTaskList
} from '@features/project-management-tools/redux/myProjectDetailsSlice';

import baseApi from '@store/api/baseApi';

import { printApiError } from '@/utils/Util';
import { closeLoader, openLoader } from '@/store/actions/LoaderAction';

export const projectManagementApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		getAllProjects: builder.mutation({
			query: ({ body, params }) => ({
				url: `/projects/all`,
				method: 'POST',
				body,
				params
			}),

			keepUnusedDataFor: 0,
			async onQueryStarted(_args, { queryFulfilled, dispatch, getState }) {
				try {
					dispatch(openLoader());
					const { notStartedProjects, inProgressProjects, completedProjects } =
						getState()?.myProjectStore;
					const result = await queryFulfilled;

					const pending = result?.data?.PENDING;
					const running = result?.data?.RUNNING;
					const completed = result?.data?.COMPLETED;

					// If search is provided, don't keep previous data
					if (_args.params.page > 0) {
						dispatch(
							setNotStartedProjects({
								...(notStartedProjects || {}),
								...(pending || {}),
								data: [...(notStartedProjects?.data || []), ...(pending?.data || [])]
							})
						);
						dispatch(
							setInProgressProjects({
								...(inProgressProjects || {}),
								...(running || {}),
								data: [...(inProgressProjects?.data || []), ...(running?.data || [])]
							})
						);
						dispatch(
							setCompletedProjects({
								...(completedProjects || {}),
								...(completed || {}),
								data: [...(completedProjects?.data || []), ...(completed?.data || [])]
							})
						);
					} else {
						dispatch(
							setNotStartedProjects(pending || { data: [], totalElements: 0, currentPage: 0 })
						);
						dispatch(
							setInProgressProjects(running || { data: [], totalElements: 0, currentPage: 0 })
						);
						dispatch(
							setCompletedProjects(completed || { data: [], totalElements: 0, currentPage: 0 })
						);
					}
				} catch (error) {
					printApiError(error?.data?.error);
				} finally {
					dispatch(closeLoader());
				}
			}
		}),

		getProjectById: builder.query({
			query: (id) => `/projects/${id}`,
			async onQueryStarted(_args, { queryFulfilled, dispatch }) {
				try {
					dispatch(openLoader());
					const result = await queryFulfilled;
					const projectDetails = result?.data || {};
					dispatch(setProjectDetails(projectDetails));
					dispatch(setProjectMembers(projectDetails?.memberResponseList || []));
					if (projectDetails?.columnVisibilityJson) {
						dispatch(
							setSelectedColumnToHide(JSON.parse(projectDetails?.columnVisibilityJson) || [])
						);
					}
				} catch (error) {
					printApiError(error?.data?.error);
				} finally {
					dispatch(closeLoader());
				}
			}
		}),

		createProject: builder.mutation({
			query: ({ body }) => ({
				url: '/projects/add',
				method: 'POST',
				body
			})
		}),

		updateProject: builder.mutation({
			query: ({ body, id }) => ({
				url: `/projects/${id}`,
				method: 'PUT',
				body
			})
		}),

		deleteProject: builder.mutation({
			query: (id) => ({
				url: `/projects/${id}`,
				method: 'DELETE'
			})
		}),
		// Project Links
		createLink: builder.mutation({
			query: ({ body, projectId }) => ({
				url: `projects/${projectId}/link`,
				method: 'POST',
				body
			})
		}),

		editLink: builder.mutation({
			query: ({ body, projectId }) => ({
				url: `projects/${projectId}/link`,
				method: 'PUT',
				body
			})
		}),

		deleteLink: builder.mutation({
			query: ({ linkId }) => ({
				url: `projects/link/${linkId}`,
				method: 'DELETE'
			})
		}),
		// Project Details Task
		fetchTasks: builder.query({
			query: ({ projectId }) => `project-tasks/${projectId}?sort=sl,asc`,
			keepUnusedDataFor: 0,
			async onQueryStarted(_args, { queryFulfilled, dispatch }) {
				try {
					dispatch(openLoader());
					const result = await queryFulfilled;
					dispatch(setTaskList(result?.data || []));
					dispatch(setOriginalTaskList(result?.data || []));
				} catch (error) {
					printApiError(error?.data?.error);
				} finally {
					dispatch(closeLoader());
				}
			}
		}),

		addTask: builder.mutation({
			query: ({ body, projectId }) => ({
				url: `project-tasks/${projectId}/add`,
				method: 'POST',
				body
			})
		}),

		updateTask: builder.mutation({
			query: ({ body, projectId }) => ({
				url: `project-tasks/${projectId}`,
				method: 'PUT',
				body
			})
		}),

		deleteTask: builder.mutation({
			query: ({ taskId }) => ({
				url: `project-tasks/${taskId}`,
				method: 'DELETE'
			})
		}),

		// Group (Project details page)
		getGroups: builder.query({
			query: ({ projectId, params }) => ({
				url: `project-tasks/${projectId}/group`,
				params,
				keepUnusedDataFor: 0
			}),
			async onQueryStarted(_args, { queryFulfilled, dispatch }) {
				try {
					dispatch(openLoader());
					const result = await queryFulfilled;
					dispatch(setGroupList(result?.data || []));
				} catch (error) {
					printApiError(error?.data?.error);
				} finally {
					dispatch(closeLoader());
				}
			}
		}),
		createGroup: builder.mutation({
			query: ({ body, projectId }) => ({
				url: `project-tasks/${projectId}/add-group`,
				method: 'POST',
				body
			})
		}),

		updateGroup: builder.mutation({
			query: ({ body }) => ({
				url: `project-tasks/group`,
				method: 'PUT',
				body
			})
		}),

		// Project Members
		addMemberToProject: builder.mutation({
			query: ({ body, projectId, userId }) => ({
				url: `/projects/${projectId}/add-member/${userId}`,
				method: 'POST',
				body
			})
		}),

		removeMemberFromProject: builder.mutation({
			query: ({ projectId, userId }) => ({
				url: `/projects/${projectId}/remove-member/${userId}`,
				method: 'POST'
			})
		}),
		// Hide Column Table view
		hideColumn: builder.mutation({
			query: ({ body, params, projectId }) => ({
				url: `/projects/${projectId}/column-visibility`,
				method: 'POST',
				body,
				params
			})
		}),

		updateTaskTag: builder.mutation({
			query: ({ body }) => ({
				url: 'projects/tag',
				method: 'PUT',
				body
			})
		}),
		getProjectTags: builder.query({
			query: ({ params, projectId }) => ({
				url: `projects/${projectId}/tags`,
				method: 'GET',
				params
			})
		}),
		// My-Project filter
		getMyProjectFilterOptions: builder.mutation({
			query: ({ body, params = '' }) => ({
				url: 'projects/filter-options',
				method: 'POST',
				body,
				params
			})
		}),
		updateOverviewAndStageVisibility: builder.mutation({
			query: ({ params, projectId }) => ({
				url: `projects/${projectId}/overview-and-stages-visibility`,
				method: 'POST',
				params
			})
		})
	})
});

export const {
	useGetAllProjectsMutation,
	useCreateProjectMutation,
	useUpdateProjectMutation,
	useDeleteProjectMutation,
	useCreateLinkMutation,
	useEditLinkMutation,
	useDeleteLinkMutation,
	useFetchTasksQuery,
	useAddTaskMutation,
	useUpdateTaskMutation,
	useDeleteTaskMutation,
	useGetGroupsQuery, // get Groups
	useCreateGroupMutation, // Create group
	useUpdateGroupMutation, // Update group
	useGetProjectByIdQuery, // Get project by id
	useAddMemberToProjectMutation, // Add member to project
	useRemoveMemberFromProjectMutation, // Remove member from project
	useHideColumnMutation,
	useUpdateTaskTagMutation,
	useGetProjectTagsQuery,
	useGetMyProjectFilterOptionsMutation,
	useUpdateOverviewAndStageVisibilityMutation
} = projectManagementApi;
